import React, { forwardRef } from "react";
import ReactSelect, { components as selectComponents, ControlProps, Props } from 'react-select'
import ReactAsyncSelect from 'react-select/async';
import ReactAsyncCreatSelect from 'react-select/async-creatable';
import './Select.scss'
import IconLocation from "@images/icons/location.svg";

export interface ListOption {
  readonly value: string;
  readonly label: string;
  readonly type?: string;
}

export const LocationIcon = ({type}: { type: string }) => {
  switch (type) {
    case 'place':
    case 'municipality':
    case 'province':
      return <img src={IconLocation} alt={''} />;
    default:
      return <></>;
  }
}

type BaseSelectProps = Omit<Props<ListOption, boolean>, 'onChange' | 'isMulti'> & {
  dataTestid?: string
  placeholder?: any
  className?: string
  isAsync?: boolean
  isCreatable?: boolean
  components?: any
  defaultOptions?: any;
  cacheOptions?: any;
  loadOptions?: any;
  isLoading?: any;
  formatCreateLabel?: (inputValue: string) => React.ReactNode;
  onCreateOption?: (inputValue: string) => void;
}

type SingleSelectProps = {
  isMulti?: false
  onChange?: (input: ListOption | null) => void
}

type MultiSelectProps = {
  isMulti: true
  onChange?: (input: ListOption[]) => void
}

export type SelectProps = BaseSelectProps & (SingleSelectProps | MultiSelectProps)

type SelectComponent = {
  (props: BaseSelectProps & SingleSelectProps & { ref?: React.Ref<any> }): React.JSX.Element
  (props: BaseSelectProps & MultiSelectProps & { ref?: React.Ref<any> }): React.JSX.Element
}

export const Select = forwardRef(({
  dataTestid,
  onChange,
  isMulti,
  isAsync,
  isCreatable,
  components,
  ...props
}: SelectProps, ref) => {
  const Control = ({children, ...controlProps}: { children: any }) => <div data-testid={dataTestid}>
    <selectComponents.Control {...controlProps as ControlProps}>{children}</selectComponents.Control>
  </div>;
  const CoreElement = isAsync ? isCreatable ? ReactAsyncCreatSelect : ReactAsyncSelect : ReactSelect
  const multiple = (isMulti ?? false) as boolean
  const {id, styles: stylesProp, options: optionsProp, ...otherProps} = props
  const options = Array.isArray(optionsProp) ? optionsProp : []

  return <CoreElement
    ref={ref as any}
    cacheOptions
    placeholder={props.placeholder || ''}
    className="rs-container"
    classNamePrefix="rs"
    isMulti={multiple}
    components={{...components, Control}}
    menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined}
    menuPosition="fixed"
    styles={{...stylesProp}}
    onChange={(input: unknown) => {
      if (!onChange) return
      if (multiple) {
        const value = Array.isArray(input) ? input : input ? [input as ListOption] : []
        ;(onChange as ((value: ListOption[]) => void))(value)
        return
      }
      const value = Array.isArray(input) ? input[0] : input
      ;(onChange as ((value: ListOption | null) => void))((value as ListOption) ?? null)
    }}
    inputId={id}
    {...otherProps}
    options={options} />

}) as SelectComponent;

export default Select

